import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, RotateCcw, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";

const PAY = { cash: "نقداً", knet: "كي نت", visa: "فيزا (سابق)", mastercard: "ماستركارد (سابق)" };
const Row = ({ k, v, testid }) => (
  <div className="flex justify-between text-sm border-b border-border py-1.5" data-testid={testid}><span>{k}</span><span className="font-medium">{v}</span></div>
);
const Card = ({ title, children }) => (
  <div className="bg-white border border-border p-4"><h2 className="text-xs tracking-luxe uppercase text-accent mb-2">{title}</h2>{children}</div>
);

export default function AdminCleanup() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/training-cleanup/preview").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const backup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/training-cleanup/backup"); toast.success(`تم إنشاء النسخة ${data.backup_id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const download = async (id) => {
    const r = await api.get(`/admin/training-cleanup/backup/${id}/download`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `rnfline-${id}.json`; a.click();
    load();
  };
  const restore = async (id) => {
    const c = window.prompt(`استعادة السجلات الناقصة فقط من النسخة (لا تُستبدل أي بيانات موجودة).\nاكتبي رقم النسخة للتأكيد: ${id}`);
    if (!c) return;
    try { const { data } = await api.post(`/admin/training-cleanup/backup/${id}/restore`, { confirm: c }); toast.success(`تمت الاستعادة: ${JSON.stringify(data.restored)}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="py-24 text-center text-text-muted">…</div>;
  const total = d.invoices.count + d.payments.web_orders + d.returns.count + d.closings.count;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="cleanup-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl leading-snug">تنظيف عمليات التدريب — تجهيز فقط</h1>
        <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3" data-testid="cleanup-no-delete-note">
          <ShieldAlert className="w-5 h-5 shrink-0" />
          <span>{d.scope_note}. هذه الصفحة للحصر والنسخ الاحتياطي فقط، ولا يوجد فيها حذف. لن تُعدَّل كميات المخزون.</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title={`الفواتير (${d.invoices.count})`}>
          <Row k="إجمالي قيمة الفواتير" v={formatKWD(d.invoices.total_value, "ar")} testid="cleanup-inv-total" />
          {Object.entries(d.invoices.by_payment_method).map(([k, v]) => <Row key={k} k={`طريقة الدفع: ${PAY[k] || k}`} v={v} />)}
          {Object.entries(d.invoices.by_status).map(([k, v]) => <Row key={k} k={`الحالة: ${k === "cancelled" ? "ملغاة" : k === "completed" ? "مكتملة" : k}`} v={v} />)}
          {Object.entries(d.invoices.by_source).map(([k, v]) => <Row key={k} k={`المصدر: ${k === "STORE" ? "المتجر" : "الموقع"}`} v={v} />)}
        </Card>
        <Card title="عمليات الدفع">
          <Row k="دفعات المحل (ضمن الفواتير)" v={d.payments.store_payments_in_invoices} testid="cleanup-store-payments" />
          <Row k="طلبات الموقع" v={d.payments.web_orders} testid="cleanup-web-orders" />
          <Row k="طلبات مدفوعة / قيد التنفيذ" v={d.payments.web_orders_paid} />
          <Row k="بانتظار الدفع / فاشلة / ملغاة" v={d.payments.web_orders_unpaid_or_cancelled} />
        </Card>
        <Card title={`المرتجعات والاستبدال (${d.returns.count})`}>
          {Object.entries(d.returns.by_status).map(([k, v]) => <Row key={k} k={{ requested: "بانتظار المراجعة", approved: "معتمد", rejected: "مرفوض" }[k] || k} v={v} />)}
          {d.returns.count === 0 && <p className="text-sm text-text-muted">لا يوجد</p>}
        </Card>
        <Card title="الإقفالات والسجلات المرتبطة">
          <Row k="الإقفالات اليومية" v={d.closings.count} testid="cleanup-closings" />
          <Row k="سجلات تدقيق مرتبطة بالعمليات" v={d.related_audit_entries} />
          <Row k="سجل الرسائل البريدية" v={d.related_email_log} />
          <Row k="عدّاد الفواتير الحالي" v={d.counters.invoice_no ?? "—"} />
          <Row k="عدّاد الطلبات الحالي" v={d.counters.order_no ?? "—"} />
        </Card>
      </div>

      <Card title="ما سيتأثر وما سيبقى عند الحذف لاحقاً">
        <p className="text-sm leading-relaxed" data-testid="cleanup-impact">
          سيتأثر: {total} عملية ({d.invoices.count} فاتورة، {d.payments.web_orders} طلب موقع، {d.returns.count} مرتجع، {d.closings.count} إقفال) مع سجلات التدقيق والرسائل المرتبطة بها،
          وستتغيّر التقارير والإحصاءات لأنها تُحسب من هذه العمليات، ويمكن تصفير العدّادات لتبدأ الأرقام من 1.
        </p>
        <p className="text-sm leading-relaxed mt-2" data-testid="cleanup-preserved">
          يبقى دون تغيير: {d.preserved.products} موديل بصورها وأسعارها، {d.preserved.users} حساب (منها حساب الأونر وكلمة مروره)، الإعدادات والسياسات، و{d.preserved.files} ملف صور.
        </p>
      </Card>

      <Card title="أثر الإزالة على المخزون (للاطلاع فقط — لم تُعدَّل أي كمية)">
        {d.stock_impact.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="cleanup-stock-none">لا يوجد أثر: العمليات الحالية لم تخصم من مخزون المقاسات (الموديلات تُفصَّل حسب الطلب أو بيعت كجاهز بدون مقاس مخزّن). حذفها لن يغيّر أي كمية.</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[560px]" data-testid="cleanup-stock-table">
            <thead><tr className="text-text-muted text-xs"><th className="text-start py-1">الموديل</th><th>المقاس/اللون</th><th>المخزون الحالي</th><th>خُصم بالعمليات</th><th>لو أُعيد</th></tr></thead>
            <tbody>{d.stock_impact.map((r, i) => (
              <tr key={i} className="border-t border-border"><td className="py-1">{r.product} <span className="text-[10px] text-text-muted">{r.sku}</span></td><td className="text-center">{r.size}/{r.color}</td><td className="text-center">{r.current_stock ?? "غير موجود"}</td><td className="text-center">{r.net_taken_by_operations}</td><td className="text-center">{r.stock_if_restored ?? "لا أثر"}</td></tr>
            ))}</tbody>
          </table>
          <p className="text-xs text-text-muted mt-2">«غير موجود» يعني أن هذا المقاس/اللون لم يعد ضمن مخزون الموديل الحالي، فلا يتأثر بأي إزالة. حذف الفواتير لا يعيد الكميات تلقائياً؛ قرار إعادة الكميات منفصل ويحتاج موافقتك.</p></div>
        )}
      </Card>

      <Card title="النسخ الاحتياطية القابلة للاستعادة">
        <button onClick={backup} disabled={busy} data-testid="cleanup-backup-btn" className="inline-flex items-center gap-2 bg-[#141110] text-white px-5 min-h-[48px] text-base font-medium disabled:opacity-50">
          <DatabaseBackup className="w-5 h-5" /> {busy ? "جارٍ إنشاء النسخة…" : "إنشاء نسخة احتياطية الآن"}
        </button>
        <p className="text-xs text-text-muted mt-2">تشمل الفواتير والطلبات والمرتجعات والإقفالات وسجلاتها والعدّادات. تُحفظ داخل النظام ويمكن تنزيلها كملف. الاستعادة تضيف الناقص فقط ولا تستبدل الموجود.</p>
        <div className="mt-3 space-y-2">
          {d.backups.map((b) => (
            <div key={b.backup_id} className="border border-border p-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`backup-${b.backup_id}`}>
              <span className="font-medium" dir="ltr">{b.backup_id}</span>
              <span className="text-xs text-text-muted">{new Date(b.created_at).toLocaleString("ar-KW")} · {b.by}</span>
              <span className="text-xs text-text-muted">فواتير {b.counts.invoices} · طلبات {b.counts.orders} · مرتجعات {b.counts.returns} · إقفالات {b.counts.closings}</span>
              <span className="flex gap-2 ms-auto">
                <button onClick={() => download(b.backup_id)} data-testid={`backup-download-${b.backup_id}`} className="inline-flex items-center gap-1 border border-[#141110] px-3 min-h-[40px]"><Download className="w-4 h-4" /> تنزيل</button>
                <button onClick={() => restore(b.backup_id)} data-testid={`backup-restore-${b.backup_id}`} className="inline-flex items-center gap-1 border border-border px-3 min-h-[40px]"><RotateCcw className="w-4 h-4" /> استعادة</button>
              </span>
            </div>
          ))}
          {d.backups.length === 0 && <p className="text-sm text-text-muted" data-testid="cleanup-no-backups">لا توجد نسخ بعد.</p>}
        </div>
      </Card>

      <Card title="سجل عملية التنظيف">
        <div className="space-y-1" data-testid="cleanup-log">
          {d.log.map((l, i) => (
            <div key={i} className="text-xs flex flex-wrap gap-2 border-b border-border py-1">
              <span className="font-medium">{{ preview: "عرض الحصر", backup: "نسخة احتياطية", download: "تنزيل نسخة", restore: "استعادة" }[l.action] || l.action}</span>
              <span className="text-text-muted">{l.actor_name}</span><span className="text-text-muted">{new Date(l.created_at).toLocaleString("ar-KW")}</span>
              {l.meta?.backup_id && <span dir="ltr">{l.meta.backup_id}</span>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
